import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import { HomepageVersionProvider } from './contexts/HomepageVersionContext';

import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Browse from './pages/Browse';
import Search from './pages/Search';
import LotDetail from './pages/LotDetail';
import Mandates from './pages/Mandates';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import Deposit from './pages/Deposit';
import Profile from './pages/Profile';
import Notifications from './pages/Notifications';
import Settings from './pages/Settings';
import Payment from './pages/Payment';
import InspectionReport from './pages/InspectionReport';
import Auction from './pages/Auction';
import SessionCalendar from './pages/SessionCalendar';
import Watchlist from './pages/Watchlist';
import BidResult from './pages/BidResult';
import DepositDetail from './pages/DepositDetail';
import MyBill from './pages/MyBill';
import Consignment from './pages/Consignment';
import ConsignmentSubmit from './pages/ConsignmentSubmit';
import ReverseApply from './pages/ReverseApply';
import ReverseTracking from './pages/ReverseTracking';
import Market from './pages/Market';
import SupportCenter from './pages/SupportCenter';
import MarketDetail from './pages/MarketDetail';
import MarketCheckout from './pages/MarketCheckout';
import MarketOrderDetail from './pages/MarketOrderDetail';
import MyCenter from './pages/MyCenter';
import BuyerMembership from './pages/BuyerMembership';
import SellerMembership from './pages/SellerMembership';
import MembershipRules from './pages/MembershipRules';
import MarketMyOrders from './pages/MarketMyOrders';
import MerchantApply from './pages/MerchantApply';
import MerchantDashboard from './pages/MerchantDashboard';
import ExhibitionList from './pages/ExhibitionList';
import ExhibitionDetail from './pages/ExhibitionDetail';
import BoothDetail from './pages/BoothDetail';
import MyAppointments from './pages/MyAppointments';
import LiveList from './pages/LiveList';
import LiveWatch from './pages/LiveWatch';
import LiveBroadcast from './pages/LiveBroadcast';
import AuctionIntro from './pages/AuctionIntro';
import BiddingCenter from './pages/BiddingCenter';
import AuctionHub from './pages/AuctionHub';
import AuctionHouseSessions from './pages/AuctionHouseSessions';
import AuctionFees from './pages/AuctionFees';
import HelpBiddingGuide from './pages/HelpBiddingGuide';
import HelpSelectionGuide from './pages/HelpSelectionGuide';
import HelpFaq from './pages/HelpFaq';
import InviteReward from './pages/InviteReward';
import ShareCard from './pages/ShareCard';
import SessionLots from './pages/SessionLots';
import CompleteProfile from './pages/CompleteProfile';
import ValuationTool from './pages/ValuationTool';
import FeeCalculator from './pages/FeeCalculator';
import Feedback from './pages/Feedback';
import Logistics from './pages/Logistics';
import PaymentVouchers from './pages/PaymentVouchers';
import WxCallback from './pages/WxCallback';
import RefLanding from './pages/RefLanding';
import Layout from './components/Layout';
import 'antd/dist/reset.css';

// 合作伙伴中心懒加载:普通用户不下载该 chunk,也不暴露功能痕迹(推广中心非公开)。
// 自愈:每次部署 chunk 哈希会变,常驻旧页面(尤其微信 webview)会按旧哈希请求到已删除的 chunk,
// nginx 旧配置会回退成 index.html(HTML 当 JS 解析→失败)导致白屏。捕获加载失败后整页刷新一次
// (index.html 为 no-store,刷新即拿到新哈希的 main.js/chunk),避免「点不开」的死白屏。
function lazyWithRetry(factory, key) {
  return lazy(async () => {
    try {
      const m = await factory();
      sessionStorage.removeItem(key);
      return m;
    } catch (err) {
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
        return new Promise(() => {}); // 挂起,等待整页刷新到新版本
      }
      throw err; // 已重试仍失败 → 交给 ErrorBoundary 展示错误
    }
  });
}
const PartnerLayout = lazyWithRetry(() => import('./pages/partner/PartnerLayout'), 'partner-chunk-reload');

function PrivateRoute({ children }) {
  const { isLoggedIn } = useAuth();
  return isLoggedIn ? children : <Navigate to="/login" replace />;
}

export default function App() {
  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        token: {
          colorPrimary: '#B8892A',
          borderRadius: 3,
          fontFamily: "'Noto Sans SC', sans-serif",
        },
      }}
    >
      <AuthProvider>
        <HomepageVersionProvider>
        <BrowserRouter>
          <ErrorBoundary>
          <Routes>
            {/* 首页统一使用共享 Layout 顶部导航(与拍品页一致),不再由 Landing 自带 header,避免跳转时导航条变动 */}
            <Route path="/" element={<Layout><Landing /></Layout>} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/wx-callback" element={<WxCallback />} />
            <Route path="/r/:code" element={<RefLanding />} />
            <Route path="/partner/*" element={
              <PrivateRoute><Suspense fallback={<div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#B8892A', fontSize: 14 }}>加载中…</div>}><PartnerLayout /></Suspense></PrivateRoute>
            } />
            <Route path="/auction-intro" element={<Layout><AuctionIntro /></Layout>} />
            <Route path="/auction-hub" element={<Layout><AuctionHub /></Layout>} />
            <Route path="/auction-hub/:houseId" element={<Layout><AuctionHouseSessions /></Layout>} />
            <Route path="/auction-fees" element={<Layout><AuctionFees /></Layout>} />
            <Route path="/browse" element={<Layout><Browse /></Layout>} />
            <Route path="/search" element={<Layout><Search /></Layout>} />
            <Route path="/lots/:id" element={<Layout><LotDetail /></Layout>} />
            <Route path="/auction" element={<Layout><Auction /></Layout>} />
            <Route path="/auction/:id" element={<Layout><Auction /></Layout>} />
            <Route path="/sessions" element={<Layout><SessionCalendar /></Layout>} />
            <Route path="/sessions/:sessionId/lots" element={<Layout><SessionLots /></Layout>} />
            <Route path="/session-calendar" element={<Layout><SessionCalendar /></Layout>} />
            <Route path="/complete-profile" element={<PrivateRoute><Layout><CompleteProfile /></Layout></PrivateRoute>} />
            <Route path="/watchlist" element={<PrivateRoute><Layout><Watchlist /></Layout></PrivateRoute>} />
            <Route path="/mandates/:id/result" element={<PrivateRoute><Layout><BidResult /></Layout></PrivateRoute>} />
            <Route path="/deposit/detail" element={<PrivateRoute><Layout><DepositDetail /></Layout></PrivateRoute>} />
            <Route path="/bills" element={<PrivateRoute><Layout><MyBill /></Layout></PrivateRoute>} />
            <Route path="/bidding-center" element={<PrivateRoute><Layout><BiddingCenter /></Layout></PrivateRoute>} />
            <Route path="/mandates" element={<PrivateRoute><Layout><Mandates /></Layout></PrivateRoute>} />
            <Route path="/orders" element={<PrivateRoute><Layout><Orders /></Layout></PrivateRoute>} />
            <Route path="/orders/:id" element={<PrivateRoute><Layout><OrderDetail /></Layout></PrivateRoute>} />
            <Route path="/orders/:id/payment" element={<PrivateRoute><Layout><Payment /></Layout></PrivateRoute>} />
            <Route path="/orders/:id/inspection" element={<PrivateRoute><Layout><InspectionReport /></Layout></PrivateRoute>} />
            <Route path="/deposit" element={<PrivateRoute><Layout><Deposit /></Layout></PrivateRoute>} />
            <Route path="/profile" element={<PrivateRoute><Layout><Profile /></Layout></PrivateRoute>} />
            <Route path="/notifications" element={<PrivateRoute><Layout><Notifications /></Layout></PrivateRoute>} />
            <Route path="/settings" element={<PrivateRoute><Layout><Settings /></Layout></PrivateRoute>} />
            <Route path="/market" element={<Layout><Market /></Layout>} />
            <Route path="/support" element={<Layout><SupportCenter /></Layout>} />
            <Route path="/market/:id" element={<Layout><MarketDetail /></Layout>} />
            <Route path="/market/:id/checkout" element={<PrivateRoute><Layout><MarketCheckout /></Layout></PrivateRoute>} />
            <Route path="/market/my-orders" element={<PrivateRoute><Layout><MarketMyOrders /></Layout></PrivateRoute>} />
            <Route path="/market/orders/:id" element={<PrivateRoute><Layout><MarketOrderDetail /></Layout></PrivateRoute>} />
            <Route path="/my-center" element={<PrivateRoute><Layout><MyCenter /></Layout></PrivateRoute>} />
            <Route path="/membership/buyer" element={<PrivateRoute><Layout><BuyerMembership /></Layout></PrivateRoute>} />
            <Route path="/membership/seller" element={<PrivateRoute><Layout><SellerMembership /></Layout></PrivateRoute>} />
            <Route path="/membership/rules" element={<PrivateRoute><Layout><MembershipRules /></Layout></PrivateRoute>} />
            <Route path="/consignment" element={<PrivateRoute><Layout><Consignment /></Layout></PrivateRoute>} />
            <Route path="/consignment/submit" element={<PrivateRoute><Layout><ConsignmentSubmit /></Layout></PrivateRoute>} />
            <Route path="/merchant/apply" element={<PrivateRoute><Layout><MerchantApply /></Layout></PrivateRoute>} />
            <Route path="/merchant/dashboard" element={<PrivateRoute><Layout><MerchantDashboard /></Layout></PrivateRoute>} />
            <Route path="/exhibitions" element={<Layout><ExhibitionList /></Layout>} />
            <Route path="/exhibitions/:id" element={<Layout><ExhibitionDetail /></Layout>} />
            <Route path="/exhibitions/:id/booth/:boothId" element={<Layout><BoothDetail /></Layout>} />
            <Route path="/my-appointments" element={<PrivateRoute><Layout><MyAppointments /></Layout></PrivateRoute>} />
            <Route path="/live" element={<Layout><LiveList /></Layout>} />
            <Route path="/live/:id" element={<Layout><LiveWatch /></Layout>} />
            <Route path="/live/broadcast/:id" element={<PrivateRoute><Layout><LiveBroadcast /></Layout></PrivateRoute>} />
            <Route path="/invite" element={<PrivateRoute><Layout><InviteReward /></Layout></PrivateRoute>} />
            <Route path="/share" element={<ShareCard />} />
            <Route path="/help/bidding-guide" element={<Layout><HelpBiddingGuide /></Layout>} />
            <Route path="/help/selection-guide" element={<Layout><HelpSelectionGuide /></Layout>} />
            <Route path="/help/faq" element={<Layout><HelpFaq /></Layout>} />
            <Route path="/valuation" element={<Layout><ValuationTool /></Layout>} />
            <Route path="/fee-calculator" element={<Layout><FeeCalculator /></Layout>} />
            <Route path="/feedback" element={<Layout><Feedback /></Layout>} />
            <Route path="/logistics" element={<Layout><Logistics /></Layout>} />
            <Route path="/payment-vouchers" element={<Layout><PaymentVouchers /></Layout>} />
            <Route path="/reverse-apply" element={<PrivateRoute><Layout><ReverseApply /></Layout></PrivateRoute>} />
            <Route path="/reverse-tracking" element={<PrivateRoute><Layout><ReverseTracking /></Layout></PrivateRoute>} />
            {/* 兜底：任何未匹配路由不再白屏，给可返回的 404 页 */}
            <Route path="*" element={
              <Layout>
                <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', textAlign: 'center', color: '#6B5840' }}>
                  <div style={{ fontSize: 44, marginBottom: 12 }}>🔍</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#1A1410', marginBottom: 6 }}>页面不存在</div>
                  <div style={{ fontSize: 13, color: '#A89070', marginBottom: 24, maxWidth: 320, lineHeight: 1.7 }}>你访问的链接可能已失效或输入有误。</div>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <button onClick={() => { if (window.history.length > 1) window.history.back(); else window.location.href = '/'; }} style={{ padding: '10px 22px', borderRadius: 8, border: '1px solid #DDD7CC', background: '#fff', color: '#6B5840', fontSize: 14, cursor: 'pointer' }}>返回上一页</button>
                    <button onClick={() => { window.location.href = '/'; }} style={{ padding: '10px 22px', borderRadius: 8, border: 'none', background: '#B8892A', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>回首页</button>
                  </div>
                </div>
              </Layout>
            } />
          </Routes>
          </ErrorBoundary>
        </BrowserRouter>
        </HomepageVersionProvider>
      </AuthProvider>
    </ConfigProvider>
  );
}
